import * as React from 'react'
import { AlertCircle, AlertTriangle, X } from 'lucide-react'
import { cn } from '@/lib/utils'

// Toasts are reserved for problems that need surfacing — errors and warnings.
// Success is left quiet (the UI already reflects it), so there's no 'success'.
type ToastVariant = 'default' | 'warning' | 'error'

interface ToastItem {
  id: number
  message: string
  variant: ToastVariant
}

interface ToastApi {
  /** Show a brief, quiet toast. Repeats of the same message collapse into one. */
  toast: (message: string, opts?: { variant?: ToastVariant }) => void
}

const ToastContext = React.createContext<ToastApi | null>(null)

/** Access the toast API. Must be rendered under <ToastProvider>. */
export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>')
  return ctx
}

let nextToastId = 0
const TOAST_MS = 3000
const MAX_VISIBLE = 4

/**
 * A minimal, quiet toast stack (Notion-style: a small bordered card that fades up
 * bottom-right, auto-dismisses, and can be clicked away). Deliberately dependency
 * free. Same-message toasts collapse to one so rapid autosaves don't pile up.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([])

  const dismiss = React.useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const toast = React.useCallback(
    (message: string, opts?: { variant?: ToastVariant }) => {
      const variant = opts?.variant ?? 'default'
      const id = ++nextToastId
      setToasts((current) => {
        const deduped = current.filter((t) => !(t.message === message && t.variant === variant))
        return [...deduped, { id, message, variant }].slice(-MAX_VISIBLE)
      })
      window.setTimeout(() => dismiss(id), TOAST_MS)
    },
    [dismiss],
  )

  const api = React.useMemo(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:left-auto sm:right-0 sm:items-end">
        {toasts.map((t) => (
          <ToastCard key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const Icon = item.variant === 'error' ? AlertCircle : item.variant === 'warning' ? AlertTriangle : null
  const iconClass = item.variant === 'warning' ? 'text-hold-ink' : 'text-danger-ink'
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto flex max-w-[420px] items-center gap-2.5 rounded-md border border-line bg-surface py-[9px] pl-3 pr-2.5 text-sm text-ink shadow-md motion-safe:animate-[ds-toast-in_150ms_var(--ds-ease-out)]"
    >
      {Icon ? <Icon className={cn('h-4 w-4 shrink-0', iconClass)} /> : null}
      <span className="min-w-0 flex-1">{item.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="grid h-6 w-6 shrink-0 place-items-center rounded-sm text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink focus-visible:focus-ds"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
